import { useEffect, useState } from 'react';
import { useIsFetching, useIsMutating } from '@tanstack/react-query';
import { INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY } from 'components/domain/customData/fields/internal/useFetchInternalCustomDataFields';
import { DELETE_INTERNAL_CUSTOM_DATA_FIELDS_MUTATION_KEY } from 'components/domain/customData/fields/internal/useDeleteInternalCustomDataField';
import { useTranslation } from 'react-i18next';
import FormWithProvider from 'components/forms/form/Form';
import FormFieldset from 'components/forms/form/FormFieldset';
import { useAutomationsTabs } from '../AutomationsTabs';
import PreArrivalFormTabHeader from './header/PreArrivalFormTabHeader';
import InformationSection from './sections/information/InformationSection';
import ExtraCustomSection from './sections/extraCustom/ExtraCustomSection';
import PreArrivalFormTabFooter from './PreArrivalFormTabFooter';
import usePreArrivalFormSubmit from './usePreArrivalFormSubmit';
import PreviewPreArrivalForm from './preview/PreviewPreArrivalForm';
import usePreArrivalFormMap from './usePreArrivalFormMap';

const PreArrivalFormTab = () => {
  const { setShowNavTabs } = useAutomationsTabs();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const preArrivalFormMap = usePreArrivalFormMap();

  const handleSubmit = usePreArrivalFormSubmit();
  const { t } = useTranslation();
  const isRefetchingData =
    useIsFetching({
      queryKey: [INTERNAL_CUSTOM_DATA_FIELDS_BASE_QUERY_KEY],
    }) > 0;
  const isDeletingCustomField =
    useIsMutating({
      mutationKey: [DELETE_INTERNAL_CUSTOM_DATA_FIELDS_MUTATION_KEY],
    }) > 0;

  useEffect(() => {
    // when the tab is unmounted, we need to make sure that the nav tabs are shown again
    return () => {
      setShowNavTabs(true);
    };
  }, []);

  const {
    primaryGuestInformationMap,
    otherGuestInformationMap,
    arrivalInformationMap,
    otherInformationMap,
    customFieldsMap,
  } = preArrivalFormMap;

  return (
    <>
      <PreArrivalFormTabHeader
        isPreviewOpen={isPreviewOpen}
        setIsPreviewOpen={setIsPreviewOpen}
      />
      <FormWithProvider
        key={JSON.stringify(preArrivalFormMap)}
        onSubmit={handleSubmit}
        dirtyChecker
        noValidate
        defaultValues={{
          ...primaryGuestInformationMap,
          ...otherGuestInformationMap,
          ...arrivalInformationMap,
          ...otherInformationMap,
          customFields: customFieldsMap,
          newCustomFields: null,
        }}
      >
        {isPreviewOpen ? (
          <PreviewPreArrivalForm />
        ) : (
          <FormFieldset disabled={isRefetchingData || isDeletingCustomField}>
            <InformationSection
              id="primary-guest-information"
              tableData={primaryGuestInformationMap}
              sectionHeader={t(
                'pageAutomations.preArrivalForm.sectionHeaders.primaryGuestInformation',
              )}
            />
            <InformationSection
              id="other-guest-information"
              tableData={otherGuestInformationMap}
              sectionHeader={t(
                'pageAutomations.preArrivalForm.sectionHeaders.otherGuestInformation',
              )}
            />
            <InformationSection
              id="arrival-information"
              tableData={arrivalInformationMap}
              sectionHeader={t(
                'pageAutomations.preArrivalForm.sectionHeaders.arrivalInformation',
              )}
            />
            <ExtraCustomSection
              extraInformationData={otherInformationMap}
              customFields={customFieldsMap || {}}
            />
            <PreArrivalFormTabFooter />
          </FormFieldset>
        )}
      </FormWithProvider>
    </>
  );
};

export default PreArrivalFormTab;
